import * as modalTypes from "../../../../constants/modalTypes";
import * as objectTypes from "../../../../constants/objectTypes";
import * as optionHelper from "../../../../helpers/optionHelper";
import * as privilegesHelper from "../../../../helpers/privilegesHelper";
import * as propertyConstants from "../../../../constants/propertyConstants";
import * as translationHelper from "../../../../helpers/translationHelper";

import React, { Component } from "react";
import { User, createEmptyUser, generateUserData, generateUserHeadings } from "../../../../types/user";

import { Company } from "../../../../types/company";
import { ModalParams } from "../../../../types/modalParams";
import { ModalProperties } from "../../../../types/modalProperties";
import { TableContainer } from "../../../Table";
import { TableHeading } from "../../../../types/tableHeading";
import { UserGroup } from "../../../../types/userGroup";

import { t as translate } from "react-i18nify";

type Props = {
    tableConstant: string;
    objectType: objectTypes.ObjectType;
    dataPrivileges: any;
    loading: boolean;
    buttonLoading: boolean;
    companyList: Array<Company>;
    loggedUser: User | null;
    userGroupList: Array<UserGroup>;
    allList: Array<User>;
    activeList: Array<User>;
    columnOrder: Array<propertyConstants.Property>;
    columnVisibility: Record<propertyConstants.Property, boolean>;
    columnWidth: Record<propertyConstants.Property, number>;
    offset: number;
    page: number;
    rowCount: number;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    showFilterRow: boolean;
    showGlobalSearch: boolean;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property | null;
    totalCount: number | null;
};

export class UserTable extends Component<Props> {
    generateTableHeadings = (modalType: modalTypes.ModalType | null = null): Array<TableHeading> => {
        const propertyList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_USERNAME];
        const requiredList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_USERNAME,
            propertyConstants.PROPERTY_USER_GROUP_LIST
        ];
        const alwaysVisibleList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_USERNAME];
        const editableList: Array<propertyConstants.Property> = [...propertyList];
        let visibleList: Array<propertyConstants.Property> = [...propertyList];

        if (modalType === modalTypes.MODAL_USERS_ADD) {
            requiredList.push(propertyConstants.PROPERTY_PASSWORD);
        }

        if (privilegesHelper.isSuperadmin(this.props.loggedUser)) {
            visibleList.splice(1, 0, propertyConstants.PROPERTY_COMPANY_ID);
            editableList.splice(1, 0, propertyConstants.PROPERTY_COMPANY_ID);
        }

        if (modalType) {
            visibleList.splice(1, 0, propertyConstants.PROPERTY_PASSWORD);
            editableList.splice(1, 0, propertyConstants.PROPERTY_PASSWORD);
            visibleList.splice(3, 0, propertyConstants.PROPERTY_USER_GROUP_LIST);
            editableList.splice(3, 0, propertyConstants.PROPERTY_USER_GROUP_LIST);
        }

        let orderList = [...visibleList];

        if (modalType === null) {
            orderList = optionHelper.handleTableHeadingOrder(visibleList, this.props.columnOrder);
            visibleList = optionHelper.handleTableHeadingVisibility(visibleList, this.props.columnVisibility);
        }

        return generateUserHeadings(
            this.props.objectType,
            this.props.companyList,
            this.props.userGroupList,
            orderList,
            visibleList,
            editableList,
            requiredList,
            alwaysVisibleList,
            this.props.columnWidth
        );
    };

    generateAddModalParams = (): ModalParams => {
        const companyId = privilegesHelper.isAdmin(this.props.loggedUser)
            ? this.props.loggedUser?.[propertyConstants.PROPERTY_COMPANY_ID] || ""
            : "";

        return new ModalParams(
            modalTypes.ADD_MODAL,
            this.props.objectType,
            translate("user.addUser"),
            createEmptyUser(companyId, this.props.userGroupList),
            this.generateTableHeadings(modalTypes.MODAL_USERS_ADD)
        );
    };

    generateEditModalParams = (): ModalParams | null => {
        const activeData = generateUserData(this.props.activeList);

        if (activeData.length) {
            return new ModalParams(
                modalTypes.EDIT_MODAL,
                this.props.objectType,
                translate("user.editUser"),
                activeData[0],
                this.generateTableHeadings(modalTypes.MODAL_USERS_EDIT)
            );
        }

        return null;
    };

    render(): JSX.Element {
        const modalProperties: ModalProperties = {
            addModalType: modalTypes.MODAL_USERS_ADD,
            addModalParams: this.generateAddModalParams(),
            editModalType: modalTypes.MODAL_USERS_EDIT,
            editModalParams: this.generateEditModalParams()
        };

        return (
            <div className="height-100">
                <TableContainer
                    className={"table-with-filter"}
                    tableConstant={this.props.tableConstant}
                    objectType={this.props.objectType}
                    dataPrivileges={this.props.dataPrivileges}
                    loading={this.props.loading}
                    buttonLoading={this.props.buttonLoading}
                    title={translationHelper.getTitleSectionTranslation(this.props.objectType)}
                    titleClassName={"title"}
                    headings={this.generateTableHeadings()}
                    data={generateUserData(this.props.allList)}
                    activeData={this.props.activeList}
                    offset={this.props.offset}
                    page={this.props.page}
                    rowCount={this.props.rowCount}
                    search={this.props.search}
                    searchParams={this.props.searchParams}
                    showFilterRow={this.props.showFilterRow}
                    showGlobalSearch={this.props.showGlobalSearch}
                    sortingAsc={this.props.sortingAsc}
                    sortingCriterion={this.props.sortingCriterion}
                    totalCount={this.props.totalCount}
                    modalProperties={modalProperties}
                />
            </div>
        );
    }
}
